import React, { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import LoginForm from './components/Auth/LoginForm';
import Sidebar from './components/Layout/Sidebar';
import Dashboard from './components/Dashboard/Dashboard';
import StudentList from './components/Students/StudentList';
import StudentDashboard from './components/Student/StudentDashboard';
import SalesPages from './components/Units/SalesPages';
import Applications from './components/Pages/Applications';
import Branches from './components/Pages/Branches';
import Units from './components/Pages/Units';
import Users from './components/Pages/Users';
import Reports from './components/Pages/Reports';
import Calendar from './components/Pages/Calendar';
import Notifications from './components/Pages/Notifications';
import Deadlines from './components/Pages/Deadlines';
import Settings from './components/Pages/Settings';
import SalesLeads from './components/Sales/SalesLeads';
import SeriousCandidates from './components/Sales/SeriousCandidates';
import SalesReports from './components/Sales/SalesReports';
import FollowUps from './components/Sales/FollowUps';
import FutureRegistrations from './components/Sales/FutureRegistrations';
import FairData from './components/Sales/FairData';
import WebsiteData from './components/Sales/WebsiteData';
import SmsSender from './components/Sales/SmsSender';
import MessagesPage from './components/Messages/MessagesPage';
import GmailIntegration from './components/Gmail/GmailIntegration';

import Contacts from './components/Pages/Contacts';
import FinancialDocuments from './components/Pages/FinancialDocuments';
import FinancialReports from './components/Pages/FinancialReports';
import Invoices from './components/Pages/Invoices';
import AccountingDashboard from './components/Accounting/AccountingDashboard';
import Contracts from './components/Pages/Contracts';
import Installments from './components/Pages/Installments';
import Expenses from './components/Pages/Expenses';
import OperationsDashboard from './components/Operations/OperationsDashboard';
import OperationsLeads from './components/Operations/OperationsLeads';
import ProgramsPage from './components/Operations/ProgramsPage';
import MeetingsPage from './components/Operations/MeetingsPage';
import TasksPage from './components/Operations/TasksPage';
import UniversitiesPage from './components/Operations/UniversitiesPage';
import PreferencesPage from './components/Operations/PreferencesPage';
import OperationsLogsPage from './components/Operations/OperationsLogsPage';
import ExamsPage from './components/Operations/ExamsPage';
import StudentProgramList from './components/Operations/StudentProgramList';
import LicenseStudentList from './components/Operations/LicenseStudentList';
import EngineeringTable from './components/Operations/EngineeringTable';
import IMATPage from './components/Operations/IMATPage';
import MedicalStudentList from './components/Operations/MedicalStudentList';
import ArchitectureTable from './components/Operations/ArchitectureTable';
import VisaDashboard from './components/Visa/VisaDashboard';
import VisaAppointmentsPage from './components/Units/VisaAppointmentsPage';
import VisaApplications from './components/Visa/VisaApplications';
import VisaDocumentManagement from './components/Visa/VisaDocumentManagement';
import VisaChecklistManagement from './components/Visa/VisaChecklistManagement';
import AddEventModal from './components/Modals/AddEventModal';
import AddLeadModal from './components/Modals/AddLeadModal';
import LeadDetailModal from './components/Modals/LeadDetailModal';
import CallNotification from './components/Karel/CallNotification';
import CallHistory from './components/Karel/CallHistory';
import { supabase } from './lib/supabase';

import Newsletter from './components/Sales/Newsletter';
import LeaveTracking from './components/Pages/LeaveTracking';

// Test Prep Imports
import TestPrepDashboard from './components/TestPrep/TestPrepDashboard';
import MockExams from './components/TestPrep/MockExams';
import CourseEnrollments from './components/TestPrep/CourseEnrollments';
import Payments from './components/TestPrep/Payments';
import PrivateLessons from './components/TestPrep/PrivateLessons';
import Coaching from './components/TestPrep/Coaching';
import DocumentServices from './components/TestPrep/DocumentServices';
import TestPrepStudents from './components/TestPrep/TestPrepStudents';
import TeacherPayments from './components/TestPrep/TeacherPayments';
import Revenue from './components/TestPrep/Revenue';
import Instructors from './components/TestPrep/Instructors';
import TeacherResponses from './components/TestPrep/TeacherResponses';
import TestPrepLeads from './components/TestPrep/TestPrepLeads';
import ConfirmDialog from './components/UI/ConfirmDialog';

// Tech Support Imports
import TechSupportDashboard from './components/Pages/TechSupportDashboard';

const AppContent: React.FC = () => {
  const { user, isLoading } = useAuth();

  // Initialize activeTab from localStorage or default logic
  // Determine the default dashboard based on user role
  const getDefaultTabForUser = (currentUser: any) => {
    if (!currentUser) return 'dashboard';
    if (currentUser.role === 'student') return 'dashboard';
    if (currentUser.role === 'unit') {
      switch (currentUser.unitType) {
        case 'visa':
        case 'visa_consultant':
          return 'visa';
        case 'education_consultant':
          return 'operations-dashboard';
        case 'sales':
          return 'sales';
        case 'accounting':
          return 'accounting';
        case 'testprep':
          return 'testprep-dashboard';
        case 'teknik_destek':
          return 'tech-support-dashboard';
        default:
          return 'dashboard';
      }
    }
    return 'dashboard';
  };

  const getInitialTab = () => {
    const storedTab = localStorage.getItem('activeTab');
    if (storedTab) return storedTab;
    return 'dashboard'; // Default fallthrough, actual user-based redirect happens in effect
  };

  const [activeTab, setActiveTabState] = useState(getInitialTab);

  // Track the last user ID to detect account switches vs refreshes
  const [lastUserId, setLastUserId] = useState<string | null>(() => {
    return localStorage.getItem('pisa_last_user_id');
  });

  const setActiveTab = (tab: string) => {
    localStorage.setItem('activeTab', tab);
    setActiveTabState(tab);
  };

  // Handle user changes and initial load redirection
  React.useEffect(() => {
    if (user) {
      if (user.id !== lastUserId) {
        // User has changed (Switch Account or New Login)
        const newDefaultTab = getDefaultTabForUser(user);
        setActiveTab(newDefaultTab);

        // Update tracking
        setLastUserId(user.id);
        localStorage.setItem('pisa_last_user_id', user.id);
      } else {
        // Same user (Refresh or Profile Update)
        // Ensure we aren't on a "wrong" dashboard if the stored tab was generic 'dashboard'
        // or if we just want to ensure validity. 
        // For now, preserving existing 'refresh keeps tab' behavior is good.
        // But if the activeTab is completely unauthorized, we might want to check that here.
        // Keeping it simple based on user request: "Only switch dashboard when account changes"
      }
    }
  }, [user, lastUserId]);

  /* Disable Karel WebPhone for now
  React.useEffect(() => {
    if (user?.karelToken) {
      console.log('Starting Karel WebPhone from App.tsx');
      // Küçük bir gecikme ekleyelim ki sayfa tam yüklensin
      setTimeout(() => {
        if (window.startKarelWebPhone) {
          window.startKarelWebPhone(user.karelToken as string);
        } else {
          console.error('window.startKarelWebPhone function not found');
        }
      }, 1000);
    }
  }, [user]);
  */
  const [showScheduleMeetingModal, setShowScheduleMeetingModal] = useState(false);
  const [currentCall, setCurrentCall] = useState<any>(null);
  const [showCallHistory, setShowCallHistory] = useState(false);
  const [callStartTime, setCallStartTime] = useState<Date | null>(null);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showLeadDetailModal, setShowLeadDetailModal] = useState(false);
  const [selectedLeadForCall, setSelectedLeadForCall] = useState<any>(null);
  const [prefilledPhoneNumber, setPrefilledPhoneNumber] = useState<string>('');
  const [lastSyncedCallId, setLastSyncedCallId] = useState<string | null>(null);

  /* Disable Karel Sync for now
  React.useEffect(() => {
    if (!user) return;

    const syncKarelCallHistory = async () => {
      try {
        // Karel'den arama geçmişini al
        const karelHistory = window.getKarelCallHistory ? window.getKarelCallHistory() : [];

        if (!karelHistory || karelHistory.length === 0) {
          return;
        }

        console.log('Karel arama geçmişi alındı:', karelHistory.length, 'arama');

        // Her aramayı kontrol et ve veritabanında yoksa kaydet
        for (const call of karelHistory) {
          const callId = call.id || call.callId;
          
          let phoneNumber = call.number || call.phoneNumber || call.remote || call.to || call.destination || 'Bilinmeyen';

          // Eğer numara Bilinmeyen ise ve giden arama ise, sessionStorage'dan al
          if (phoneNumber === 'Bilinmeyen' && call.direction === 'outgoing') {
            const storedNumber = sessionStorage.getItem('karelCurrentCallNumber');
            if (storedNumber) {
              phoneNumber = storedNumber;
            }
          }

          // Bu aramayı daha önce senkronize ettik mi kontrol et
          if (lastSyncedCallId && callId && callId <= lastSyncedCallId) {
            continue; // Daha önce işlenmiş
          }

          // Veritabanında bu arama var mı kontrol et
          const { data: existingCall } = await supabase
            .from('call_logs')
            .select('id')
            .eq('user_id', user.id)
            .eq('phone_number', phoneNumber)
            .eq('start_time', new Date(call.time || call.startTime).toISOString())
            .maybeSingle();

          if (existingCall) {
            continue;
          }

          // Aramayı veritabanına kaydet
          const startTime = new Date(call.time || call.startTime);
          const endTime = call.endTime ? new Date(call.endTime) : startTime;
          const duration = call.duration || Math.floor((endTime.getTime() - startTime.getTime()) / 1000);

          // Lead var mı kontrol et
          const { data: lead } = await supabase
            .from('leads')
            .select('id')
            .or(`phone.eq.${phoneNumber},phone.like.%${phoneNumber.replace(/\D/g, '')}%`)
            .limit(1)
            .maybeSingle();

          const direction = call.direction === 'incoming' ? 'incoming' : 'outgoing';
          const status = call.status === 'ended' || call.status === 'answered' ? 'answered' :
            call.status === 'missed' ? 'missed' :
              call.status === 'busy' ? 'busy' :
                call.status === 'rejected' ? 'rejected' : 'answered';

          const { error } = await supabase.from('call_logs').insert({
            user_id: user.id,
            phone_number: phoneNumber,
            caller_number: direction === 'incoming' ? phoneNumber : null,
            receiver_number: direction === 'outgoing' ? phoneNumber : null,
            caller_user_id: direction === 'outgoing' ? user.id : null,
            receiver_user_id: direction === 'incoming' ? user.id : null,
            direction: direction,
            status: status,
            duration: duration,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            lead_id: lead?.id || null
          });

          if (error) {
            console.error('Karel araması kaydedilemedi:', error);
          }
        }

        // Son senkronize edilen arama ID'sini güncelle
        if (karelHistory.length > 0) {
          const lastCall = karelHistory[karelHistory.length - 1];
          const lastId = lastCall.id || lastCall.callId;
          if (lastId) {
            setLastSyncedCallId(lastId);
          }
        }
      } catch (error) {
        console.error('Karel arama geçmişi senkronizasyon hatası:', error);
      }
    };

    // İlk senkronizasyon
    syncKarelCallHistory();

    // Her 30 saniyede bir senkronize et
    const syncInterval = setInterval(syncKarelCallHistory, 30000);

    return () => clearInterval(syncInterval);
  }, [user, lastSyncedCallId]);
  */

  // Event listener'ları ekle
  React.useEffect(() => {
    const handleOpenScheduleMeetingModal = () => setShowScheduleMeetingModal(true);
    const handleChangeTab = (e: any) => setActiveTab(e.detail);

    const saveCallToDatabase = async (callData: any) => {
      if (!user) {
        console.log('Kullanıcı bulunamadı, arama kaydedilemiyor');
        return;
      }

      try {
        const endTime = new Date();
        const startTime = callStartTime || endTime;
        const duration = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
        const phoneNumber = callData.number || callData.phoneNumber || 'Bilinmeyen';

        console.log('Arama kaydediliyor:', {
          phoneNumber,
          direction: callData.direction,
          status: callData.status,
          duration,
          startTime: callStartTime,
          endTime
        });

        const { data: lead } = await supabase
          .from('leads')
          .select('id')
          .eq('phone', phoneNumber)
          .maybeSingle();

        const direction = callData.direction || 'outgoing';
        const { data, error } = await supabase.from('call_logs').insert({
          user_id: user.id,
          phone_number: phoneNumber,
          caller_number: direction === 'incoming' ? phoneNumber : null,
          receiver_number: direction === 'outgoing' ? phoneNumber : null,
          caller_user_id: direction === 'outgoing' ? user.id : null,
          receiver_user_id: direction === 'incoming' ? user.id : null,
          direction: direction,
          status: callData.status || 'answered',
          duration: duration,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          lead_id: lead?.id || null
        }).select();

        if (error) {
          console.error('Arama kaydı hatası:', error);
        } else {
          console.log('Arama başarıyla kaydedildi:', data);
        }
      } catch (error) {
        console.error('Arama veritabanına kaydedilemedi:', error);
      }
    };

    const checkPhoneNumberAndOpenModal = async (phoneNumber: string) => {
      if (!phoneNumber || phoneNumber === 'Bilinmeyen') return;

      try {
        console.log('Telefon numarası kontrol ediliyor:', phoneNumber);

        // Telefon numarasını temizle (sadece rakamlar)
        const cleanedNumber = phoneNumber.replace(/\D/g, '');

        // Dahili numara kontrolü (3-4 haneli numaralar)
        if (cleanedNumber.length >= 3 && cleanedNumber.length <= 4) {
          console.log('Dahili numara tespit edildi:', cleanedNumber);

          // Dahili kullanıcıyı profil tablosundan ara
          const { data: profile } = await supabase
            .from('profiles')
            .select('id, full_name, email, role, branch_id')
            .eq('karel_extension', cleanedNumber)
            .eq('is_active', true)
            .maybeSingle();

          if (profile) {
            console.log('Dahili kullanıcı bulundu:', profile);
            // Dahili arama için özel bir bildirim gösterilebilir
            // Şu an için sadece log'layalım
            return;
          }

          // Dahili numara ama kullanıcı bulunamadı
          console.log('Dahili numara kullanıcıya ait değil:', cleanedNumber);
          return;
        }

        // Normal telefon numarası - Lead kontrolü yap
        const { data: leads, error } = await supabase
          .from('leads')
          .select('*')
          .or(`phone.eq.${phoneNumber},phone.eq.${cleanedNumber},phone.like.%${cleanedNumber}%`)
          .limit(1);

        if (error) {
          console.error('Lead kontrol hatası:', error);
          return;
        }

        if (leads && leads.length > 0) {
          // Kayıtlı lead bulundu, detay modalını aç
          console.log('Kayıtlı lead bulundu:', leads[0]);
          setSelectedLeadForCall(leads[0]);
          setShowLeadDetailModal(true);
        } else {
          // Kayıtlı lead yok, yeni lead ekleme modalını aç
          console.log('Kayıtlı lead bulunamadı, yeni lead eklenecek');
          setPrefilledPhoneNumber(phoneNumber);
          setShowAddLeadModal(true);
        }
      } catch (error) {
        console.error('Lead kontrol hatası:', error);
      }
    };

    const extractPhoneNumber = (data: any): string | null => {
      // Tüm olası telefon numarası alanlarını kontrol et
      const possibleFields = [
        data.number,
        data.phoneNumber,
        data.phone,
        data.remoteNumber,
        data.remote,
        data.to,
        data.from,
        data.callee,
        data.caller,
        data.displayName,
        data.uri,
        data.sipUri
      ];

      for (const field of possibleFields) {
        if (field) {
          let cleanNumber = field.toString();
          // SIP URI'den numarayı çıkar (örn: sip:+905551234567@...)
          if (cleanNumber.includes('@')) {
            cleanNumber = cleanNumber.split('@')[0].replace('sip:', '').replace('tel:', '');
          }
          // Sadece rakam ve + karakteri bırak
          const onlyNumbers = cleanNumber.replace(/[^\d+]/g, '');

          // Dahili numaralar (3-4 haneli) veya normal numaralar (10+ haneli)
          if (onlyNumbers.length >= 3) {
            return onlyNumbers;
          }
        }
      }
      return null;
    };

    const handleOutgoingCall = async (e: any) => {
      console.log('Karel OutgoingCall event:', e.detail);
      console.log('Karel OutgoingCall ALL KEYS:', Object.keys(e.detail || {}));

      setCallStartTime(new Date());

      // Telefon numarasını çıkar
      const phoneNumber = extractPhoneNumber(e.detail);
      console.log('📞 Extracted phone number (outgoing):', phoneNumber);

      if (phoneNumber) {
        sessionStorage.setItem('karelCurrentCallNumber', phoneNumber);
      }

      setCurrentCall({
        ...e.detail,
        number: phoneNumber,
        phoneNumber: phoneNumber,
        status: 'outgoing',
        direction: 'outgoing'
      });

      if (phoneNumber) {
        await checkPhoneNumberAndOpenModal(phoneNumber);
      }
    };

    const handleRinging = async (e: any) => {
      const callData = e.detail;
      console.log('Karel Ringing event:', callData);
      console.log('Karel Ringing ALL KEYS:', Object.keys(callData || {}));

      if (!callStartTime) {
        setCallStartTime(new Date());
      }

      const phoneNumber = extractPhoneNumber(callData);
      console.log('📞 Extracted phone number (ringing):', phoneNumber);

      if (phoneNumber) {
        sessionStorage.setItem('karelCurrentCallNumber', phoneNumber);
      }

      if (callData.direction === 'incoming') {
        setCurrentCall({
          ...callData,
          number: phoneNumber,
          phoneNumber: phoneNumber,
          status: 'incoming',
          direction: 'incoming'
        });

        if (phoneNumber) {
          await checkPhoneNumberAndOpenModal(phoneNumber);
        }
      }
    };

    const handleIncomingCall = async (e: any) => {
      console.log('Karel IncomingCall event:', e.detail);
      console.log('Karel IncomingCall ALL KEYS:', Object.keys(e.detail || {}));

      if (!callStartTime) {
        setCallStartTime(new Date());
      }

      const phoneNumber = extractPhoneNumber(e.detail);
      console.log('📞 Extracted phone number (incoming):', phoneNumber);

      if (phoneNumber) {
        sessionStorage.setItem('karelCurrentCallNumber', phoneNumber);
      }

      setCurrentCall({
        ...e.detail,
        number: phoneNumber,
        phoneNumber: phoneNumber,
        status: 'incoming',
        direction: 'incoming'
      });

      if (phoneNumber) {
        await checkPhoneNumberAndOpenModal(phoneNumber);
      }
    };

    const handleCallAnswered = (e: any) => {
      console.log('Karel CallAnswered event:', e.detail);
      setCurrentCall((prev: any) => prev ? { ...prev, status: 'active' } : null);
    };

    const handleCallEnded = async (e: any) => {
      const callData = e.detail;
      console.log('Karel CallEnded event:', callData);
      console.log('Karel CallEnded event keys:', Object.keys(callData || {}));
      console.log('Karel CallEnded full detail:', JSON.stringify(callData, null, 2));

      // Telefon numarasını çıkar
      let phoneNumber = extractPhoneNumber(callData);

      // Eğer bulunamadıysa session storage ve currentCall'dan dene
      if (!phoneNumber) {
        const sessionNumber = sessionStorage.getItem('karelCurrentCallNumber');
        phoneNumber = sessionNumber || currentCall?.number || currentCall?.phoneNumber || null;
      }

      console.log('📞 Extracted phone number (ended):', phoneNumber);
      console.log('Session number:', sessionStorage.getItem('karelCurrentCallNumber'));
      console.log('Current call:', currentCall);

      // Hala numara bulunamadıysa 'Bilinmeyen' kullan
      const finalPhoneNumber = phoneNumber || 'Bilinmeyen';

      // Status'ü belirle - Karel'den gelen tüm alanları kontrol et
      let callStatus = 'answered';

      // Önce callData içindeki tüm alanları kontrol et
      const statusFields = [
        callData.status,
        callData.callStatus,
        callData.state,
        callData.callState,
        callData.reason,
        callData.hangupCause,
        callData.terminationReason,
        callData.disposition
      ];

      console.log('🔍 Status fields:', statusFields);

      for (const field of statusFields) {
        if (!field) continue;
        const lowerField = field.toString().toLowerCase();

        if (lowerField.includes('busy') || lowerField.includes('meşgul') || lowerField === '486') {
          callStatus = 'busy';
          break;
        } else if (lowerField.includes('reject') || lowerField.includes('red') || lowerField.includes('decline') || lowerField === '603') {
          callStatus = 'rejected';
          break;
        } else if (lowerField.includes('miss') || lowerField.includes('cevapsız') || lowerField.includes('noanswer') || lowerField === '480' || lowerField === '408') {
          callStatus = 'missed';
          break;
        } else if (lowerField.includes('fail') || lowerField.includes('başarısız')) {
          callStatus = 'failed';
          break;
        } else if (lowerField.includes('cancel') || lowerField === '487') {
          callStatus = 'cancelled';
          break;
        }
      }

      // Eğer arama hiç cevaplandırılmadıysa (CallAnswered event olmadı)
      if (callStatus === 'answered' && (!currentCall || currentCall.status !== 'active')) {
        // Gelen arama cevaplandırılmadıysa missed
        if (currentCall?.status === 'incoming') {
          callStatus = 'missed';
        } else if (currentCall?.status === 'outgoing' || currentCall?.direction === 'outgoing') {
          // Giden arama cevaplandırılmadıysa no-answer veya busy
          callStatus = 'no-answer';
        }
      }

      console.log('✅ Call status determined:', callStatus);

      await saveCallToDatabase({
        ...callData,
        number: finalPhoneNumber,
        phoneNumber: finalPhoneNumber,
        status: callStatus
      });

      // Session storage'ı temizle
      sessionStorage.removeItem('karelCurrentCallNumber');
      setCurrentCall(null);
      setCallStartTime(null);
    };

    const handleCallFailed = async (e: any) => {
      const callData = e.detail;
      console.log('Karel CallFailed event:', callData);
      console.log('Karel CallFailed event keys:', Object.keys(callData || {}));

      let phoneNumber = extractPhoneNumber(callData);
      if (!phoneNumber) {
        const sessionNumber = sessionStorage.getItem('karelCurrentCallNumber');
        phoneNumber = sessionNumber || currentCall?.number || currentCall?.phoneNumber || null;
      }
      const finalPhoneNumber = phoneNumber || 'Bilinmeyen';
      console.log('📞 Extracted phone number (failed):', finalPhoneNumber);

      await saveCallToDatabase({
        ...callData,
        number: finalPhoneNumber,
        phoneNumber: finalPhoneNumber,
        status: callData.status || 'failed'
      });

      sessionStorage.removeItem('karelCurrentCallNumber');
      setCurrentCall(null);
      setCallStartTime(null);
    };

    const handleHangup = async (e: any) => {
      console.log('Karel Hangup event:', e.detail);
      console.log('Karel Hangup event keys:', Object.keys(e.detail || {}));

      let phoneNumber = extractPhoneNumber(e.detail);
      if (!phoneNumber) {
        const sessionNumber = sessionStorage.getItem('karelCurrentCallNumber');
        phoneNumber = sessionNumber || currentCall?.number || currentCall?.phoneNumber || null;
      }
      const finalPhoneNumber = phoneNumber || 'Bilinmeyen';
      console.log('📞 Extracted phone number (hangup):', finalPhoneNumber);

      await saveCallToDatabase({
        ...e.detail,
        number: finalPhoneNumber,
        phoneNumber: finalPhoneNumber,
        status: 'answered'
      });

      sessionStorage.removeItem('karelCurrentCallNumber');
      setCurrentCall(null);
      setCallStartTime(null);
    };

    const handleCallBusy = async (e: any) => {
      console.log('Karel CallBusy event:', e.detail);

      let phoneNumber = extractPhoneNumber(e.detail);
      if (!phoneNumber) {
        const sessionNumber = sessionStorage.getItem('karelCurrentCallNumber');
        phoneNumber = sessionNumber || currentCall?.number || currentCall?.phoneNumber || null;
      }
      const finalPhoneNumber = phoneNumber || 'Bilinmeyen';
      console.log('📞 Extracted phone number (busy):', finalPhoneNumber);

      await saveCallToDatabase({
        ...e.detail,
        number: finalPhoneNumber,
        phoneNumber: finalPhoneNumber,
        status: 'busy'
      });

      sessionStorage.removeItem('karelCurrentCallNumber');
      setCurrentCall(null);
      setCallStartTime(null);
    };

    const handleCallRejected = async (e: any) => {
      console.log('Karel CallRejected event:', e.detail);

      let phoneNumber = extractPhoneNumber(e.detail);
      if (!phoneNumber) {
        const sessionNumber = sessionStorage.getItem('karelCurrentCallNumber');
        phoneNumber = sessionNumber || currentCall?.number || currentCall?.phoneNumber || null;
      }
      const finalPhoneNumber = phoneNumber || 'Bilinmeyen';
      console.log('📞 Extracted phone number (rejected):', finalPhoneNumber);

      await saveCallToDatabase({
        ...e.detail,
        number: finalPhoneNumber,
        phoneNumber: finalPhoneNumber,
        status: 'rejected'
      });

      sessionStorage.removeItem('karelCurrentCallNumber');
      setCurrentCall(null);
      setCallStartTime(null);
    };

    const handleCallMissed = async (e: any) => {
      console.log('Karel CallMissed event:', e.detail);

      let phoneNumber = extractPhoneNumber(e.detail);
      if (!phoneNumber) {
        const sessionNumber = sessionStorage.getItem('karelCurrentCallNumber');
        phoneNumber = sessionNumber || currentCall?.number || currentCall?.phoneNumber || null;
      }
      const finalPhoneNumber = phoneNumber || 'Bilinmeyen';
      console.log('📞 Extracted phone number (missed):', finalPhoneNumber);

      await saveCallToDatabase({
        ...e.detail,
        number: finalPhoneNumber,
        phoneNumber: finalPhoneNumber,
        status: 'missed'
      });

      sessionStorage.removeItem('karelCurrentCallNumber');
      setCurrentCall(null);
      setCallStartTime(null);
    };

    // Karel'in TÜM event'lerini yakalamak için genel listener
    const handleAllKarelEvents = (e: any) => {
      if (e.type.startsWith('karel')) {
        console.log('🔔 KAREL EVENT:', e.type, 'Detail:', e.detail);
        console.log('🔔 KAREL EVENT KEYS:', Object.keys(e.detail || {}));
        console.log('🔔 KAREL EVENT JSON:', JSON.stringify(e.detail, null, 2));
      }
    };

    // Tüm karel event'lerini dinle
    const karelEvents = [
      'karelOutgoingCall', 'karelRinging', 'karelIncomingCall', 'karelAnswered',
      'karelCallEnded', 'karelCallFailed', 'karelCallBusy', 'karelCallRejected',
      'karelCallMissed', 'karelHangup', 'karelConnected', 'karelDisconnected',
      'karelError', 'karelBusy', 'karelRejected', 'karelNoAnswer', 'karelTerminated'
    ];

    karelEvents.forEach(event => {
      window.addEventListener(event, handleAllKarelEvents);
    });

    window.addEventListener('openScheduleMeetingModal', handleOpenScheduleMeetingModal);
    window.addEventListener('changeTab', handleChangeTab);
    window.addEventListener('karelOutgoingCall', handleOutgoingCall);
    window.addEventListener('karelRinging', handleRinging);
    window.addEventListener('karelIncomingCall', handleIncomingCall);
    window.addEventListener('karelAnswered', handleCallAnswered);
    window.addEventListener('karelCallEnded', handleCallEnded);
    window.addEventListener('karelCallFailed', handleCallFailed);
    window.addEventListener('karelCallBusy', handleCallBusy);
    window.addEventListener('karelCallRejected', handleCallRejected);
    window.addEventListener('karelCallMissed', handleCallMissed);
    window.addEventListener('karelHangup', handleHangup);

    return () => {
      karelEvents.forEach(event => {
        window.removeEventListener(event, handleAllKarelEvents);
      });

      window.removeEventListener('openScheduleMeetingModal', handleOpenScheduleMeetingModal);
      window.removeEventListener('changeTab', handleChangeTab);
      window.removeEventListener('karelOutgoingCall', handleOutgoingCall);
      window.removeEventListener('karelRinging', handleRinging);
      window.removeEventListener('karelIncomingCall', handleIncomingCall);
      window.removeEventListener('karelAnswered', handleCallAnswered);
      window.removeEventListener('karelCallEnded', handleCallEnded);
      window.removeEventListener('karelCallFailed', handleCallFailed);
      window.removeEventListener('karelCallBusy', handleCallBusy);
      window.removeEventListener('karelCallRejected', handleCallRejected);
      window.removeEventListener('karelCallMissed', handleCallMissed);
      window.removeEventListener('karelHangup', handleHangup);
    };
  }, [user, callStartTime]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  // Öğrenci için özel dashboard
  if (user.role === 'student') {
    return <StudentDashboard />;
  }

  const renderContent = () => {
    console.log('=== RENDER CONTENT ===');
    console.log('Active Tab:', activeTab);
    console.log('User Role:', user.role);
    console.log('User Unit Type:', user.unitType);

    if (user.unitType === 'accounting') {
      if (activeTab === 'dashboard') return <AccountingDashboard />;
      if (activeTab === 'students') return <StudentList />;
      if (activeTab === 'contracts') return <Contracts />;
      if (activeTab === 'installments') return <Installments />;
      if (activeTab === 'expenses') return <Expenses />;
      if (activeTab === 'leave-tracking') return <LeaveTracking />;
      if (activeTab === 'financial-documents') return <FinancialDocuments />;
      if (activeTab === 'financial-reports') return <FinancialReports />;
    }

    if (user.role === 'unit') {
      switch (user.unitType) {
        case 'sales':
          if (activeTab === 'sales') return <SalesPages />;
          if (activeTab === 'dashboard') return <SalesPages />;
          if (activeTab === 'sales-leads') return <SalesLeads />;
          if (activeTab === 'serious-candidates') return <SeriousCandidates />;
          if (activeTab === 'follow-ups') return <FollowUps />;
          if (activeTab === 'future-registrations') return <FutureRegistrations />;
          if (activeTab === 'contracts') return <Contracts />;
          if (activeTab === 'sales-reports') return <SalesReports />;
          if (activeTab === 'website-data') return <WebsiteData />;
          if (activeTab === 'gmail') return <GmailIntegration />;
          if (activeTab === 'calendar') return <Calendar />;
          if (activeTab === 'newsletter') return <Newsletter />;
          break;
        case 'accounting':
          if (activeTab === 'accounting') return <AccountingDashboard />;
          if (activeTab === 'dashboard') return <AccountingDashboard />;
          if (activeTab === 'contracts') return <Contracts />;
          if (activeTab === 'installments') return <Installments />;
          if (activeTab === 'expenses') return <Expenses />;
          if (activeTab === 'leave-tracking') return <LeaveTracking />;
          if (activeTab === 'financial-documents') return <FinancialDocuments />;
          if (activeTab === 'financial-reports') return <FinancialReports />;
          break;
        case 'education_consultant':
          if (activeTab === 'dashboard' || activeTab === 'operations-dashboard') return <OperationsDashboard onNavigate={setActiveTab} />;
          if (activeTab === 'operations-leads') return <OperationsLeads />;
          if (activeTab === 'all-engineering') return <EngineeringTable programType="all-engineering" />;
          if (activeTab === 'engineering') return <StudentProgramList programType="engineering" />;
          if (activeTab === 'polito') return <EngineeringTable programType="polito" />;
          if (activeTab === 'polimi') return <EngineeringTable programType="polimi" />;
          if (activeTab === 'imat') return <IMATPage />;
          if (activeTab === 'license') return <LicenseStudentList />;
          if (activeTab === 'architecture') return <ArchitectureTable />;
          if (activeTab === 'all-medical') return <MedicalStudentList programType="all-medical" />;
          if (activeTab === 'san-raffaele') return <MedicalStudentList programType="san-raffaele" />;
          if (activeTab === 'humanitas') return <MedicalStudentList programType="humanitas" />;
          if (activeTab === 'cattolica') return <MedicalStudentList programType="cattolica" />;
          if (activeTab === 'biomedico') return <MedicalStudentList programType="biomedico" />;
          if (activeTab === 'unicamillus') return <MedicalStudentList programType="unicamillus" />;
          if (activeTab === 'universities') return <UniversitiesPage />;
          if (activeTab === 'logs') return <OperationsLogsPage />;
          if (activeTab === 'preferences') return <PreferencesPage />;
          if (activeTab === 'programs') return <ProgramsPage />;
          if (activeTab === 'meetings') return <MeetingsPage />;
          if (activeTab === 'tasks') return <TasksPage />;
          if (activeTab === 'exams') return <ExamsPage />;
          break;
        case 'testprep':
          if (activeTab === 'dashboard' || activeTab === 'testprep-dashboard') return <TestPrepDashboard onNavigate={setActiveTab} />;
          if (activeTab === 'testprep-leads') return <TestPrepLeads />;
          if (activeTab === 'mock-exams') return <MockExams />;
          if (activeTab === 'course-enrollments') return <CourseEnrollments />;
          if (activeTab === 'payments') return <Payments />;
          if (activeTab === 'private-lessons') return <PrivateLessons />;
          if (activeTab === 'coaching') return <Coaching />;
          if (activeTab === 'document-services') return <DocumentServices />;
          if (activeTab === 'students') return <TestPrepStudents />;
          if (activeTab === 'teacher-payments') return <TeacherPayments />;
          if (activeTab === 'instructors') return <Instructors />;
          if (activeTab === 'teacher-responses') return <TeacherResponses />;
          if (activeTab === 'revenue') return <Revenue />;
          if (activeTab === 'sms-sender') return <SmsSender />;
          if (activeTab === 'messages') return <MessagesPage />;
          if (activeTab === 'settings') return <Settings />;
          break;

        case 'visa_consultant':
          if (activeTab === 'dashboard') return <VisaDashboard />;
          if (activeTab === 'visa') return <VisaDashboard />;
          // if (activeTab === 'visa-appointments') return <VisaAppointmentsPage />; // Added below
          if (activeTab === 'visa-appointments') return <VisaAppointmentsPage />;
          if (activeTab === 'visa-applications') return <VisaApplications />;
          if (activeTab === 'visa-documents') return <VisaDocumentManagement />;
          if (activeTab === 'visa-checklists') return <VisaChecklistManagement />;
          if (activeTab === 'sms-sender') return <SmsSender />;
          if (activeTab === 'students') return <StudentList />;
          break;
        case 'teknik_destek':
          if (activeTab === 'dashboard' || activeTab === 'tech-support-dashboard') return <TechSupportDashboard />;
          if (activeTab === 'support-tickets') return <TechSupportDashboard />;
          if (activeTab === 'user-management') return <Users />;
          if (activeTab === 'settings') return <Settings />;
          break;
        default:
          if (activeTab === 'dashboard') return <div className="p-8 text-center text-gray-500">Bu birim için özel sayfa geliştiriliyor...</div>;
          break;
      }
    }

    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'students':
        return <StudentList />;
      case 'sales-leads':
        return <SalesLeads />;
      case 'follow-ups':
        return <FollowUps />;
      case 'future-registrations':
        return <FutureRegistrations />;
      case 'contracts':
        return <Contracts />;
      case 'fair-data':
        return <FairData />;
      case 'website-data':
        return <WebsiteData />;
      case 'sales-reports':
        return <SalesReports />;
      case 'sms-sender':
        return <SmsSender />;
      case 'calendar':
        return <Calendar />;
      case 'applications':
        return <Applications />;
      case 'branches':
        return <Branches />;
      case 'units':
        return <Units />;
      case 'users':
        return <Users />;
      case 'reports':
        return <Reports />;
      case 'notifications':
        return <Notifications />;
      case 'deadlines':
        return <Deadlines />;
      case 'contacts':
        return <Contacts />;

      case 'testprep-leads':
        return <TestPrepLeads />;
      case 'messages':
        return <MessagesPage />;
      case 'financial-documents':
        return <FinancialDocuments />;
      case 'financial-reports':
        return <FinancialReports />;
      case 'invoices':
        return <Invoices />;
      case 'settings':
        return <Settings />;

      default:
        return <Dashboard />;
    }
  };

  const isOperationsWorkspace = user?.unitType === 'education_consultant';

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-950 flex ${isOperationsWorkspace ? 'operations-workspace' : ''}`}>
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className={`flex-1 overflow-auto ${isOperationsWorkspace ? 'operations-main' : 'p-6'}`}>
        {renderContent()}
      </main>

      {/* Global Modals */}
      <AddEventModal
        isOpen={showScheduleMeetingModal}
        onClose={() => setShowScheduleMeetingModal(false)}
        onSave={(eventData) => {
          console.log('Yeni görüşme planlandı:', eventData);
          setShowScheduleMeetingModal(false);
          alert('Görüşme başarıyla planlandı!');
        }}
      />

      <AddLeadModal
        isOpen={showAddLeadModal}
        onClose={() => {
          setShowAddLeadModal(false);
          setPrefilledPhoneNumber('');
        }}
        onSave={async (leadData) => {
          try {
            const { data, error } = await supabase
              .from('leads')
              .insert(leadData)
              .select()
              .single();

            if (error) {
              console.error('Lead ekleme hatası:', error);
              return { ok: false, error: error.message || 'Lead kaydedilemedi' };
            }

            console.log('Yeni lead başarıyla kaydedildi:', data);

            setShowAddLeadModal(false);
            setPrefilledPhoneNumber('');

            window.dispatchEvent(new Event('newLeadAdded'));

            return { ok: true, data };
          } catch (err: any) {
            console.error('Lead kaydetme hatası:', err);
            return { ok: false, error: err?.message || 'Beklenmeyen hata oluştu' };
          }
        }}
        prefilledPhone={prefilledPhoneNumber}
      />

      {selectedLeadForCall && (
        <LeadDetailModal
          isOpen={showLeadDetailModal}
          onClose={() => {
            setShowLeadDetailModal(false);
            setSelectedLeadForCall(null);
          }}
          lead={selectedLeadForCall}
          onEdit={() => { }} // Dummy onEdit if required, or remove if optional. Interface said onEdit is required? 
        />
      )}

      {/* Disable Karel Call Notification components for now
      <CallNotification
        call={currentCall}
        onAnswer={() => {
          if (window.answerKarelCall) {
            window.answerKarelCall();
          }
        }}
        onHangup={() => {
          console.log('=== App.tsx onHangup called ===');
          if (window.hangupKarelCall) {
            const result = window.hangupKarelCall();
            console.log('hangupKarelCall result:', result);
          }
          // Immediately clear the call state to close the modal
          setCurrentCall(null);
          setCallStartTime(null);
          sessionStorage.removeItem('karelCurrentCallNumber');
          console.log('✓ Call state cleared - modal should close');
        }}
      />
      <CallHistory
        isOpen={showCallHistory}
        onClose={() => setShowCallHistory(false)}
        onCallNumber={(number) => {
          if (window.makeKarelCall) {
            window.makeKarelCall(number);
          }
          setShowCallHistory(false);
        }}
      />
      */}
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
        <ConfirmDialog />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
